/**
 * PublicMapPage - Main public landing page with asbestos reporting map
 *
 * Features:
 * - Full-screen map (no header)
 * - Search for municipality with partial matching
 * - URL parameter support for sharing (?ine=28079)
 * - Display asbestos inventory (read-only) in blue
 * - Click anywhere on map to place marker and report
 * - Login button
 */

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { IconArrowLeft, IconExternalLink, IconLogin, IconMap, IconSearch, IconUpload } from '@tabler/icons-react'
import { Feature } from 'ol'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  CloseButton,
  Combobox,
  Divider,
  FileButton,
  Group,
  Image,
  Loader,
  Modal,
  Paper,
  ScrollArea,
  Stack,
  Text,
  Textarea,
  TextInput,
  useCombobox,
  useMantineColorScheme,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { API_ORIGIN } from '@/config/api'
import { getPublicAoi, submitReport, type PublicReportPayload } from '@/routes/publicReports'
import ScaleLine from 'ol/control/ScaleLine'
import GeoJSON from 'ol/format/GeoJSON'
import { Point } from 'ol/geom'
import TileLayer from 'ol/layer/Tile'
import VectorLayer from 'ol/layer/Vector'
import Map from 'ol/Map'
import { fromLonLat, toLonLat } from 'ol/proj'
import VectorSource from 'ol/source/Vector'
import XYZ from 'ol/source/XYZ'
import { Circle as CircleStyle, Fill, Stroke, Style } from 'ol/style'
import View from 'ol/View'

import { fetchWithTimeout } from '@/services/fetchWithTimeout'
import '../Map/MapComponent.scss'

const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY || ''

// Colors from theme.ts
const BLUE = '#4d4dff' // rgb(77, 77, 255)
const GREEN = '#00ff00'

// Palette shared with the private map's `amianto-censo` SLD so switching
// between the two apps doesn't feel visually inconsistent.
//   MCA   → purple  (confirmed asbestos)
//   MPCA  → magenta (probable asbestos)
//   NOMCA → gray    (no asbestos)
//   default (missing / legacy) → light purple, matches the SLD ElseFilter
const PRESENCE_STYLE: Record<string, [string, string]> = {
  MCA: ['rgba(128, 92, 215, 0.7)', '#4d3a8c'],
  MPCA: ['rgba(236, 72, 153, 0.7)', '#9d2b6c'],
  NOMCA: ['rgba(156, 163, 175, 0.7)', '#6b7280'],
  _fallback: ['rgba(128, 92, 215, 0.5)', '#4d3a8c'],
}

// Labels shown in the popup. Keep them plain (i18n happens in `t()` when
// this file is next refactored to use the shared translation function).
const PRESENCE_LABEL: Record<string, string> = {
  MCA: 'Contiene amianto',
  MPCA: 'Probable amianto',
  NOMCA: 'No contiene amianto',
}

// Municipality with coordinates for map centering
interface Municipality {
  name: string
  ine: string
  lat?: number
  lng?: number
}

// Cadastral data from Catastro API
interface CatastralData {
  refCatastral: string
  address?: string
  // Extended data from DNPRC endpoint
  clase?: string // UR (Urbano) or RU (Rústico)
  uso?: string // Main use (Residencial, Industrial, etc.)
  superficie?: number // Built surface in m²
  añoConstruccion?: number // Construction year
  provincia?: string
  municipio?: string
}

// Pad INE code with leading zeros for API queries (5 digits)
const padIne = (ine: string): string => {
  return ine.padStart(5, '0')
}

// Search Spanish municipalities using OpenDataSoft API
const searchMunicipalities = async (query: string): Promise<Municipality[]> => {
  if (!query || query.length < 2) {
    return []
  }

  try {
    const url = `https://public.opendatasoft.com/api/records/1.0/search/?dataset=georef-spain-municipio&q=${encodeURIComponent(query)}&rows=20`
    const response = await fetchWithTimeout(url)
    const data = await response.json()

    return (
      data.records?.map((r: any) => ({
        name: r.fields.mun_name,
        ine: r.fields.mun_code,
        lat: r.fields.geo_point_2d?.[0],
        lng: r.fields.geo_point_2d?.[1],
      })) || []
    )
  } catch {
    return []
  }
}

// Reverse geocode coordinates to address using Nominatim
const reverseGeocode = async (lat: number, lng: number): Promise<string> => {
  try {
    const response = await fetchWithTimeout(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`,
      { headers: { 'Accept-Language': 'es' } }
    )
    const data = await response.json()
    if (data.display_name) {
      return data.display_name
    }
    return ''
  } catch {
    return ''
  }
}

// Get cadastral data from Catastro API (XML response)
const getCatastralDataByCoords = async (lat: number, lng: number): Promise<CatastralData | null> => {
  try {
    // Step 1: Get cadastral reference from coordinates
    const coordUrl = `https://ovc.catastro.meh.es/ovcservweb/OVCSWLocalizacionRC/OVCCoordenadas.asmx/Consulta_RCCOOR?SRS=EPSG:4326&Coordenada_X=${lng}&Coordenada_Y=${lat}`
    const coordResponse = await fetchWithTimeout(coordUrl)
    const coordXml = await coordResponse.text()

    const parser = new DOMParser()
    const coordDoc = parser.parseFromString(coordXml, 'text/xml')

    // Helper to get element text (handles namespaced XML)
    const getTagText = (doc: Document, tagName: string): string | null => {
      const elements = doc.getElementsByTagName(tagName)
      return elements.length > 0 ? elements[0].textContent : null
    }

    // Check for errors
    const cuerr = getTagText(coordDoc, 'cuerr')
    if (cuerr && cuerr !== '0') {
      return null
    }

    // Extract cadastral reference
    const pc1 = getTagText(coordDoc, 'pc1')
    const pc2 = getTagText(coordDoc, 'pc2')
    if (!pc1 || !pc2) {
      return null
    }

    const refCatastral = pc1 + pc2
    const ldt = getTagText(coordDoc, 'ldt')

    // Step 2: Get detailed data using DNPRC endpoint
    const detailUrl = `https://ovc.catastro.meh.es/ovcservweb/OVCSWLocalizacionRC/OVCCallejero.asmx/Consulta_DNPRC?Provincia=&Municipio=&RC=${refCatastral}`
    const detailResponse = await fetchWithTimeout(detailUrl)
    const detailXml = await detailResponse.text()
    const detailDoc = parser.parseFromString(detailXml, 'text/xml')

    // Extract detailed info
    const cn = getTagText(detailDoc, 'cn') // Clase: UR (Urbano) or RU (Rústico)
    const luso = getTagText(detailDoc, 'luso') // Uso principal
    const sfc = getTagText(detailDoc, 'sfc') // Superficie construida
    const ant = getTagText(detailDoc, 'ant') // Año construcción
    const np = getTagText(detailDoc, 'np') // Provincia
    const nm = getTagText(detailDoc, 'nm') // Municipio

    return {
      refCatastral,
      address: ldt || undefined,
      clase: cn || undefined,
      uso: luso || undefined,
      superficie: sfc ? parseInt(sfc, 10) : undefined,
      añoConstruccion: ant ? parseInt(ant, 10) : undefined,
      provincia: np || undefined,
      municipio: nm || undefined,
    }
  } catch {
    return null
  }
}

// Get municipality by INE code
const getMunicipalityByIne = async (ine: string): Promise<Municipality | null> => {
  try {
    const paddedIne = padIne(ine)
    const url = `https://public.opendatasoft.com/api/records/1.0/search/?dataset=georef-spain-municipio&q=&rows=1&refine.mun_code=${encodeURIComponent(paddedIne)}`
    const response = await fetchWithTimeout(url)
    const data = await response.json()

    if (data.records?.length > 0) {
      const r = data.records[0]
      return {
        name: r.fields.mun_name,
        ine: r.fields.mun_code,
        lat: r.fields.geo_point_2d?.[0],
        lng: r.fields.geo_point_2d?.[1],
      }
    }
    return null
  } catch {
    return null
  }
}

export default function PublicMapPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const mapRef = useRef<HTMLDivElement>(null)
  const mapInstanceRef = useRef<Map | null>(null)
  const aoiLayerRef = useRef<VectorLayer<VectorSource> | null>(null)
  const asbestosLayerRef = useRef<VectorLayer<VectorSource> | null>(null)
  const markerLayerRef = useRef<VectorLayer<VectorSource> | null>(null)

  // State
  const [searchResults, setSearchResults] = useState<Municipality[]>([])
  const [searchValue, setSearchValue] = useState('')
  const [selectedMuni, setSelectedMuni] = useState<Municipality | null>(null)
  const [loading, setLoading] = useState(false)
  const [searching, setSearching] = useState(false)
  const [mapReady, setMapReady] = useState(false)

  // Combobox state
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  })

  // Debounced search
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Modal state - three modes: inventory detail, cadastral info, report form
  const [modalOpen, setModalOpen] = useState(false)
  const [modalMode, setModalMode] = useState<'inventory' | 'cadastral' | 'report'>('cadastral')
  const [inventoryProps, setInventoryProps] = useState<Record<string, any> | null>(null)
  const [selectedCoords, setSelectedCoords] = useState<{ lat: number; lng: number } | null>(null)
  const [cadastralData, setCadastralData] = useState<CatastralData | null>(null)
  const [cadastralLoading, setCadastralLoading] = useState(false)
  const [address, setAddress] = useState('')
  const [addressLoading, setAddressLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [images, setImages] = useState<{ file: File; preview: string }[]>([])
  // Contact details (optional)
  const [reporterName, setReporterName] = useState('')
  const [reporterEmail, setReporterEmail] = useState('')
  const [reporterPhone, setReporterPhone] = useState('')
  const [dataProtectionAccepted, setDataProtectionAccepted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Email validation helper
  const isValidEmail = (email: string): boolean => {
    if (!email) {
      return true
    } // Empty is valid (field is optional)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  // Helper to extract error message from API response
  const getErrorMessage = (err: any, fallback: string): string => {
    const detail = err?.response?.data?.detail
    if (!detail) {
      return fallback
    }
    if (typeof detail === 'string') {
      return detail
    }
    if (Array.isArray(detail)) {
      return detail.map((e: any) => e.msg || e.message || JSON.stringify(e)).join(', ')
    }
    if (typeof detail === 'object' && detail.msg) {
      return detail.msg
    }
    return fallback
  }

  const emailValid = isValidEmail(reporterEmail)

  // Phone validation helper
  const isValidPhone = (phone: string): boolean => {
    if (!phone) {
      return true
    } // Empty is valid (field is optional)
    // Allow digits, spaces, dashes, parentheses, plus sign. Must have 6-15 digits.
    const digitsOnly = phone.replace(/\D/g, '')
    const validChars = /^[0-9\s\-()+=]+$/.test(phone)
    return validChars && digitsOnly.length >= 6 && digitsOnly.length <= 15
  }

  const phoneValid = isValidPhone(reporterPhone)

  // Force dark mode for public page (no user = always dark)
  const { setColorScheme } = useMantineColorScheme()
  useLayoutEffect(() => {
    setColorScheme('dark')
  }, [setColorScheme])

  // Ref to track selectedMuni for map click handler
  const selectedMuniRef = useRef<Municipality | null>(null)
  useEffect(() => {
    selectedMuniRef.current = selectedMuni
  }, [selectedMuni])

  // Handle search input with debounce
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value)

      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }

      if (value.length >= 2) {
        setSearching(true)
        searchTimeoutRef.current = setTimeout(async () => {
          const results = await searchMunicipalities(value)
          setSearchResults(results)
          setSearching(false)
          combobox.openDropdown()
        }, 300)
      } else {
        setSearchResults([])
        setSearching(false)
      }
    },
    [combobox]
  )

  // Load reCAPTCHA script
  useEffect(() => {
    if (!RECAPTCHA_SITE_KEY) {
      return
    }
    const script = document.createElement('script')
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`
    script.async = true
    document.body.appendChild(script)
    return () => {
      document.body.removeChild(script)
    }
  }, [])

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) {
      return
    }

    // AOI layer for municipality boundary
    const aoiSource = new VectorSource()
    const aoiLayer = new VectorLayer({
      source: aoiSource,
      style: new Style({
        stroke: new Stroke({ color: GREEN, width: 2 }),
        fill: new Fill({ color: 'rgba(0, 255, 0, 0)' }),
      }),
    })
    aoiLayerRef.current = aoiLayer

    // Asbestos inventory layer — coloured per `is_asbestos` so the public
    // map matches the private platform's SLD (`amianto-censo`):
    //   MCA   → purple (confirmed)
    //   MPCA  → magenta (probable)
    //   NOMCA → gray (not asbestos)
    //   unknown → light purple fallback
    const asbestosSource = new VectorSource()
    const asbestosLayer = new VectorLayer({
      source: asbestosSource,
      style: (feature) => {
        const kind = feature.get('is_asbestos')
        const [fill, stroke] = PRESENCE_STYLE[kind] || PRESENCE_STYLE._fallback
        return new Style({
          image: new CircleStyle({
            radius: 6,
            fill: new Fill({ color: fill }),
            stroke: new Stroke({ color: stroke, width: 2 }),
          }),
          fill: new Fill({ color: fill }),
          stroke: new Stroke({ color: stroke, width: 2 }),
        })
      },
    })
    asbestosLayerRef.current = asbestosLayer

    // Marker layer for user-selected location
    const markerSource = new VectorSource()
    const markerLayer = new VectorLayer({
      source: markerSource,
      style: new Style({
        image: new CircleStyle({
          radius: 12,
          fill: new Fill({ color: BLUE }),
          stroke: new Stroke({ color: '#fff', width: 3 }),
        }),
      }),
    })
    markerLayerRef.current = markerLayer

    // Create map
    const map = new Map({
      target: mapRef.current,
      layers: [
        new TileLayer({
          source: new XYZ({
            url: 'https://tms-pnoa-ma.idee.es/1.0.0/pnoa-ma/{z}/{x}/{-y}.jpeg',
            maxZoom: 19,
          }),
        }),
        aoiLayer,
        asbestosLayer,
        markerLayer,
      ],
      view: new View({
        center: fromLonLat([-3.7, 40.4]),
        zoom: 6,
      }),
      controls: [
        new ScaleLine({
          units: 'metric',
          bar: true,
          steps: 1,
          text: true,
          minWidth: 100,
        }),
      ],
    })

    mapInstanceRef.current = map

    // Handle map click - check for asbestos feature first, then cadastral lookup
    map.on('click', async (evt) => {
      // Only allow clicking when a municipality is selected (use ref for closure)
      if (!selectedMuniRef.current) {
        return
      }

      // Check if user clicked on an asbestos inventory feature
      if (asbestosLayerRef.current) {
        const hit = map.forEachFeatureAtPixel(evt.pixel, (feature) => feature, {
          layerFilter: (layer) => layer === asbestosLayerRef.current,
        })
        if (hit) {
          const props = (hit as Feature).getProperties()
          const { geometry: _geom, ...displayProps } = props

          // Capture click coordinates and place marker
          const coords = toLonLat(evt.coordinate)
          setSelectedCoords({ lat: coords[1], lng: coords[0] })
          if (markerLayerRef.current) {
            const source = markerLayerRef.current.getSource()
            source?.clear()
            const marker = new Feature({ geometry: new Point(evt.coordinate) })
            source?.addFeature(marker)
          }
          // Pre-fill address from feature properties
          setAddress(displayProps.address || '')

          setInventoryProps(displayProps)
          setModalMode('inventory')
          setModalOpen(true)
          return
        }
      }

      // Check if click is within AOI boundary
      if (aoiLayerRef.current) {
        const aoiSource = aoiLayerRef.current.getSource()
        const features = aoiSource?.getFeatures() || []

        if (features.length === 0) {
          notifications.show({
            title: t('publicMap.noAoiTitle', 'Sin límites disponibles'),
            message: t(
              'publicMap.noAoiMessage',
              'Este municipio no tiene datos geográficos disponibles.'
            ),
            color: 'orange',
          })
          return
        }

        let isWithinAoi = false
        for (const feature of features) {
          const geometry = feature.getGeometry()
          if (geometry && geometry.intersectsCoordinate(evt.coordinate)) {
            isWithinAoi = true
            break
          }
        }

        if (!isWithinAoi) {
          notifications.show({
            title: t('publicMap.outsideBoundaryTitle', 'Fuera del límite'),
            message: t(
              'publicMap.outsideBoundaryMessage',
              'Por favor, haga clic dentro del límite del municipio seleccionado.'
            ),
            color: 'orange',
          })
          return
        }
      }

      // Place marker at clicked location
      const coords = toLonLat(evt.coordinate)
      const lat = coords[1]
      const lng = coords[0]
      setSelectedCoords({ lat, lng })

      // Update marker on map
      if (markerLayerRef.current) {
        const source = markerLayerRef.current.getSource()
        source?.clear()
        const marker = new Feature({
          geometry: new Point(evt.coordinate),
        })
        source?.addFeature(marker)
      }

      // Open modal in cadastral mode first
      setModalMode('cadastral')
      setModalOpen(true)
      setCadastralLoading(true)
      setCadastralData(null)
      setAddressLoading(true)
      setAddress('')

      // Fetch cadastral data and reverse geocode in parallel
      const [cadastral, addressResult] = await Promise.all([
        getCatastralDataByCoords(lat, lng),
        reverseGeocode(lat, lng),
      ])

      setCadastralData(cadastral)
      setAddress(addressResult)
      setCadastralLoading(false)
      setAddressLoading(false)
    })

    // Cursor change - pointer on asbestos features, crosshair otherwise
    map.on('pointermove', (evt) => {
      const target = map.getTarget() as HTMLElement
      if (!selectedMuniRef.current) {
        target.style.cursor = ''
        return
      }
      const hit = map.forEachFeatureAtPixel(evt.pixel, () => true, {
        layerFilter: (layer) => layer === asbestosLayerRef.current,
      })
      target.style.cursor = hit ? 'pointer' : 'crosshair'
    })

    setMapReady(true)

    return () => {
      map.setTarget(undefined)
      mapInstanceRef.current = null
    }
  }, [])

  // Update cursor when selectedMuni changes
  useEffect(() => {
    if (mapInstanceRef.current) {
      const target = mapInstanceRef.current.getTarget() as HTMLElement
      if (target) {
        target.style.cursor = selectedMuni ? 'crosshair' : ''
      }
    }
  }, [selectedMuni])

  // Handle municipality selection
  const handleMunicipalitySelect = useCallback(
    async (muni: Municipality) => {
      if (!mapReady || !mapInstanceRef.current) {
        return
      }

      setSelectedMuni(muni)
      setSearchValue(muni.name)
      combobox.closeDropdown()

      // Update URL with INE code for sharing
      setSearchParams({ ine: muni.ine })

      setLoading(true)

      // Clear previous layers
      if (aoiLayerRef.current) {
        aoiLayerRef.current.getSource()?.clear()
      }
      if (asbestosLayerRef.current) {
        asbestosLayerRef.current.getSource()?.clear()
      }
      if (markerLayerRef.current) {
        markerLayerRef.current.getSource()?.clear()
      }

      try {
        const aoi = await getPublicAoi(muni.ine)

        let hasZoomed = false

        if (aoi && aoi.geometry && aoiLayerRef.current) {
          const source = aoiLayerRef.current.getSource()
          if (source) {
            const geoJsonData = { type: 'Feature', geometry: aoi.geometry, properties: {} }
            const format = new GeoJSON()
            const features = format.readFeatures(geoJsonData, { featureProjection: 'EPSG:3857' })

            if (features.length > 0) {
              source.addFeatures(features)
              const extent = source.getExtent()
              if (extent && extent[0] !== Infinity) {
                mapInstanceRef.current.getView().fit(extent, {
                  padding: [100, 100, 100, 100],
                  maxZoom: 16,
                  duration: 800,
                })
                hasZoomed = true
              }
            }
          }
        }

        if (!hasZoomed && muni.lat && muni.lng) {
          mapInstanceRef.current.getView().animate({
            center: fromLonLat([muni.lng, muni.lat]),
            zoom: 13,
            duration: 800,
          })
        }

        await fetchAsbestosData(muni.ine)
      } catch (err) {
        if (muni.lat && muni.lng && mapInstanceRef.current) {
          mapInstanceRef.current.getView().animate({
            center: fromLonLat([muni.lng, muni.lat]),
            zoom: 13,
            duration: 800,
          })
        }
      } finally {
        setLoading(false)
      }
    },
    [mapReady, setSearchParams, combobox]
  )

  // Load municipality from URL parameter
  useEffect(() => {
    const ineFromUrl = searchParams.get('ine')
    if (ineFromUrl && mapReady && !selectedMuni) {
      getMunicipalityByIne(ineFromUrl).then((muni) => {
        if (muni) {
          handleMunicipalitySelect(muni)
        }
      })
    }
  }, [searchParams, mapReady, selectedMuni, handleMunicipalitySelect])

  // Deep-link to a specific building: `?n_ref=<catref>`. Runs after the
  // asbestos layer has features (i.e. after fetchAsbestosData). Zoom in
  // on the matching feature; if none matches, no-op so the caller sees
  // the whole municipality view.
  useEffect(() => {
    const nRef = searchParams.get('n_ref')
    if (!nRef || !mapReady || !asbestosLayerRef.current || !mapInstanceRef.current) return
    const source = asbestosLayerRef.current.getSource()
    if (!source) return
    const features = source.getFeatures()
    if (features.length === 0) return
    const target = features.find(
      (f) => (f.get('reference') || '').toString().toUpperCase() === nRef.toUpperCase(),
    )
    if (target && target.getGeometry()) {
      mapInstanceRef.current.getView().fit(target.getGeometry()!.getExtent(), {
        padding: [80, 80, 80, 80],
        maxZoom: 19,
        duration: 500,
      })
    }
  }, [searchParams, mapReady, selectedMuni])

  // Fetch asbestos inventory data from public API
  const fetchAsbestosData = useCallback(async (ine: string) => {
    if (!asbestosLayerRef.current) {
      return
    }

    try {
      // Cachebust so intermediate proxies or the browser can't hand back a
      // stale response — the private platform mutates these features and
      // the public viewer needs the latest colouring.
      const response = await fetchWithTimeout(
        `${API_ORIGIN}/api/public/asbestos/${ine}?t=${Date.now()}`,
      )
      if (response.ok) {
        const data = await response.json()
        const format = new GeoJSON()
        const features = data.features && data.features.length > 0
          ? format.readFeatures(data, {
              dataProjection: 'EPSG:4326',
              featureProjection: 'EPSG:3857',
            })
          : []
        asbestosLayerRef.current.getSource()?.clear()
        if (features.length) {
          asbestosLayerRef.current.getSource()?.addFeatures(features)

          // Deep-link a un edificio concreto: ?n_ref=<catref>. Antes vivía
          // en un useEffect con deps [searchParams, mapReady, selectedMuni]
          // pero cuando arrancaba, esta llamada async aún no había poblado
          // la capa → se retornaba con 0 features y el efecto no volvía a
          // dispararse (ninguna dep cambia al llegar los features). Al
          // hacer el fit aquí, justo tras addFeatures, la race desaparece.
          const nRef = searchParams.get('n_ref')
          if (nRef && mapInstanceRef.current) {
            const target = features.find(
              (f) => (f.get('reference') || '').toString().toUpperCase() === nRef.toUpperCase(),
            )
            if (target && target.getGeometry()) {
              mapInstanceRef.current.getView().fit(target.getGeometry()!.getExtent(), {
                padding: [80, 80, 80, 80],
                maxZoom: 19,
                duration: 500,
              })
            }
          }
        }
      }
    } catch (err) {
      // Silently fail
    }
    // `searchParams` en las deps para que el closure lea el valor actual
    // de `?n_ref=` al hacer el fit tras cargar features. Sin él, el
    // deep-link solo funciona la primera vez.
  }, [searchParams])

  // Auto-refresh the asbestos layer while a municipality is selected:
  //   * every 60 s (background poll)
  //   * whenever the tab regains focus (visibilitychange)
  // Both paths reuse fetchAsbestosData with a cache-busted URL.
  useEffect(() => {
    if (!selectedMuni?.ine) return undefined
    const ine = selectedMuni.ine
    const interval = setInterval(() => {
      fetchAsbestosData(ine)
    }, 60_000)
    const onVisibility = () => {
      if (document.visibilityState === 'visible') fetchAsbestosData(ine)
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [selectedMuni?.ine, fetchAsbestosData])

  // Handle image selection
  const handleImageSelect = useCallback(
    (files: File[]) => {
      const newImages = files.slice(0, 5 - images.length).map((file) => ({
        file,
        preview: URL.createObjectURL(file),
      }))
      setImages((prev) => [...prev, ...newImages].slice(0, 5))
    },
    [images.length]
  )

  const removeImage = useCallback((index: number) => {
    setImages((prev) => {
      const newImages = [...prev]
      URL.revokeObjectURL(newImages[index].preview)
      newImages.splice(index, 1)
      return newImages
    })
  }, [])

  const imagesToBase64 = async (): Promise<string[]> => {
    const promises = images.map(
      ({ file }) =>
        new Promise<string>((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result as string)
          reader.onerror = reject
          reader.readAsDataURL(file)
        })
    )
    return Promise.all(promises)
  }

  const getRecaptchaToken = async (): Promise<string> => {
    if (!RECAPTCHA_SITE_KEY) {
      return 'no-recaptcha-configured'
    }
    return new Promise((resolve, reject) => {
      ;(window as any).grecaptcha.ready(() => {
        ;(window as any).grecaptcha
          .execute(RECAPTCHA_SITE_KEY, { action: 'submit_report' })
          .then(resolve)
          .catch(reject)
      })
    })
  }

  const handleSubmitReport = async () => {
    if (!selectedMuni || !dataProtectionAccepted) {
      return
    }

    setSubmitting(true)
    setError(null)

    try {
      const captchaToken = await getRecaptchaToken()
      const base64Images = images.length > 0 ? await imagesToBase64() : []

      const payload: PublicReportPayload = {
        municipality_ine: selectedMuni.ine,
        address: address || undefined,
        coordinates: selectedCoords || undefined,
        message: message || undefined,
        reporter_name: reporterName || undefined,
        reporter_email: reporterEmail || undefined,
        reporter_phone: reporterPhone || undefined,
        images: base64Images,
        captcha_token: captchaToken,
      }

      await submitReport(payload)

      notifications.show({
        title: t('publicReport.successTitle', 'Reporte enviado'),
        message: t('publicReport.successMessage', 'El municipio ha sido notificado.'),
        color: 'green',
      })

      resetReportForm()
      setModalOpen(false)
    } catch (err: any) {
      const errorMessage = getErrorMessage(
        err,
        t('publicReport.errorSubmitting', 'Error al enviar el reporte')
      )
      setError(errorMessage)
    } finally {
      setSubmitting(false)
    }
  }

  const resetReportForm = () => {
    setAddress('')
    setAddressLoading(false)
    setMessage('')
    images.forEach((img) => URL.revokeObjectURL(img.preview))
    setImages([])
    setReporterName('')
    setReporterEmail('')
    setReporterPhone('')
    setDataProtectionAccepted(false)
    setSelectedCoords(null)
    setError(null)
    setCadastralData(null)
    setCadastralLoading(false)
    setInventoryProps(null)
    setModalMode('cadastral')
    // Clear marker from map
    if (markerLayerRef.current) {
      markerLayerRef.current.getSource()?.clear()
    }
  }

  const handleCloseModal = () => {
    resetReportForm()
    setModalOpen(false)
  }

  // Validation - need location, data protection, and valid email/phone if provided
  const canSubmit =
    dataProtectionAccepted && selectedCoords && selectedMuni && emailValid && phoneValid

  // Logo URL from backend static files
  const logoUrl = `${(import.meta.env.VITE_API_BASE_URL || '').replace(/\/+$/, '')}/static/2.svg`

  return (
    <Box style={{ height: '100vh', width: '100vw', position: 'relative', overflow: 'hidden' }}>
      {/* Map container */}
      <Box ref={mapRef} className="map-container" style={{ height: '100%', width: '100%' }} />

      {/* Logo - top right corner */}
      <Box
        style={{
          position: 'absolute',
          top: 16,
          right: 16,
          zIndex: 1000,
          width: '22vh',
          maxWidth: 200,
          minWidth: 120,
        }}
      >
        <img
          src={logoUrl}
          alt="Asbestos.ai"
          style={{
            width: '100%',
            height: 'auto',
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))',
          }}
        />
      </Box>

      {/* Search overlay - dark glass style */}
      <Paper
        shadow="lg"
        p="sm"
        radius="md"
        style={{
          position: 'absolute',
          top: 16,
          left: 16,
          zIndex: 1000,
          width: 340,
          background: 'rgba(0, 0, 0, 0.75)',
          backdropFilter: 'blur(8px)',
          border: '1px solid rgba(0, 255, 0, 0.3)',
        }}
      >
        <Group justify="space-between" mb="xs">
          <Group gap="xs">
            <IconMap size={20} color={GREEN} />
            <Text fw={600} size="sm" c={GREEN}>
              {t('publicMap.title', 'Mapa de Amianto')}
            </Text>
          </Group>
          <Button
            variant="subtle"
            size="xs"
            color="green"
            leftSection={<IconLogin size={14} />}
            onClick={() => navigate('/login')}
            styles={{ root: { color: GREEN } }}
          >
            {t('publicMap.login', 'Iniciar sesión')}
          </Button>
        </Group>

        <Combobox
          store={combobox}
          withinPortal
          onOptionSubmit={(val) => {
            const muni = searchResults.find((m) => m.ine === val)
            if (muni) {
              handleMunicipalitySelect(muni)
            }
          }}
        >
          <Combobox.Target>
            <TextInput
              placeholder={t('publicMap.searchPlaceholder', 'Buscar municipio...')}
              value={searchValue}
              onChange={(e) => handleSearchChange(e.currentTarget.value)}
              onFocus={() => searchResults.length > 0 && combobox.openDropdown()}
              leftSection={<IconSearch size={16} color={GREEN} />}
              rightSection={searching || loading ? <Loader size="xs" color="green" /> : null}
              styles={{
                input: {
                  background: 'rgba(0, 0, 0, 0.5)',
                  border: '1px solid rgba(0, 255, 0, 0.3)',
                  color: '#fff',
                  '&::placeholder': { color: 'rgba(255, 255, 255, 0.5)' },
                },
              }}
            />
          </Combobox.Target>

          <Combobox.Dropdown
            style={{
              background: 'rgba(0, 0, 0, 0.9)',
              border: '1px solid rgba(0, 255, 0, 0.3)',
              zIndex: 1100,
            }}
          >
            <Combobox.Options>
              <ScrollArea.Autosize mah={300}>
                {searchResults.length > 0 ? (
                  searchResults.map((m) => (
                    <Combobox.Option value={m.ine} key={m.ine} style={{ color: '#fff' }}>
                      <Group gap="xs">
                        <Text size="sm">{m.name}</Text>
                        <Badge size="xs" variant="outline" color="green">
                          {m.ine}
                        </Badge>
                      </Group>
                    </Combobox.Option>
                  ))
                ) : searchValue.length >= 2 && !searching ? (
                  <Combobox.Empty style={{ color: 'rgba(255,255,255,0.5)' }}>
                    {t('publicMap.noResults', 'Sin resultados')}
                  </Combobox.Empty>
                ) : searchValue.length < 2 ? (
                  <Combobox.Empty style={{ color: 'rgba(255,255,255,0.5)' }}>
                    {t('publicMap.typeToSearch', 'Escribe al menos 2 caracteres')}
                  </Combobox.Empty>
                ) : null}
              </ScrollArea.Autosize>
            </Combobox.Options>
          </Combobox.Dropdown>
        </Combobox>

        {selectedMuni && (
          <Box mt="xs">
            <Text size="xs" c="rgba(255,255,255,0.7)">
              {t('publicMap.selectedMunicipality', 'Seleccionado:')}{' '}
              <Text span c={GREEN} fw={600}>
                {selectedMuni.name}
              </Text>
            </Text>
            <Text size="xs" c="rgba(255,255,255,0.5)" mt={2}>
              {t('publicMap.clickToReport', 'Click en el mapa para marcar una localización')}
            </Text>
          </Box>
        )}
      </Paper>

      {/* Legend - simplified for blue layer */}
      {selectedMuni && (
        <Paper
          shadow="md"
          p="xs"
          radius="md"
          style={{
            position: 'absolute',
            bottom: 16,
            right: 16,
            zIndex: 1000,
            background: 'rgba(0, 0, 0, 0.75)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(0, 255, 0, 0.3)',
          }}
        >
          <Text size="xs" fw={500} c={GREEN} mb={4}>
            {t('publicMap.legend', 'Leyenda')}
          </Text>
          <Stack gap={2}>
            <Group gap={6}>
              <Box w={12} h={12} bg="#805cd7" style={{ borderRadius: 2 }} />
              <Text size="xs" c="#fff">
                {t('publicMap.presenceMCA', 'Contiene amianto')}
              </Text>
            </Group>
            <Group gap={6}>
              <Box w={12} h={12} bg="#ec4899" style={{ borderRadius: 2 }} />
              <Text size="xs" c="#fff">
                {t('publicMap.presenceMPCA', 'Probable amianto')}
              </Text>
            </Group>
            <Group gap={6}>
              <Box w={12} h={12} bg="#9ca3af" style={{ borderRadius: 2 }} />
              <Text size="xs" c="#fff">
                {t('publicMap.presenceNOMCA', 'No contiene amianto')}
              </Text>
            </Group>
            <Group gap={6}>
              <Box w={12} h={12} bg={GREEN} style={{ borderRadius: 2, opacity: 0.3 }} />
              <Text size="xs" c="#fff">
                {t('publicMap.municipalityBoundary', 'Límite municipal')}
              </Text>
            </Group>
          </Stack>
        </Paper>
      )}

      {/* Modal - Two-step: cadastral info first, then report form */}
      <Modal
        opened={modalOpen}
        onClose={handleCloseModal}
        title={modalMode === 'inventory'
          ? t('publicMap.inventoryTitle', 'Información del Inventario')
          : modalMode === 'cadastral'
            ? t('publicMap.cadastralTitle', 'Información Catastral')
            : t('publicMap.reportTitle', 'Reportar Amianto')
        }
        size="lg"
        centered
        styles={{
          content: {
            background: 'rgba(0, 0, 0, 0.85)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(0, 255, 0, 0.3)',
          },
          header: {
            background: 'transparent',
            borderBottom: '1px solid rgba(0, 255, 0, 0.2)',
          },
          title: {
            color: GREEN,
            fontWeight: 600,
          },
          close: {
            color: 'rgba(255, 255, 255, 0.7)',
            '&:hover': { background: 'rgba(255, 255, 255, 0.1)' },
          },
          body: {
            background: 'transparent',
          },
        }}
      >
        {/* Inventory Detail View */}
        {modalMode === 'inventory' && inventoryProps && (
          <ScrollArea.Autosize mah="70vh">
          <Stack gap="md">
            {/* Facade photo */}
            {inventoryProps.Link_image && (
              <Box
                style={{
                  borderRadius: 8,
                  overflow: 'hidden',
                  border: '1px solid rgba(0, 255, 0, 0.3)',
                  background: 'rgba(0, 0, 0, 0.3)',
                }}
              >
                <img
                  src={inventoryProps.Link_image}
                  alt="Fachada"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '200px',
                    objectFit: 'contain',
                    display: 'block',
                    margin: '0 auto',
                  }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none'
                  }}
                />
              </Box>
            )}

            {/* All fields in a consistent layout */}
            {[
              // Presence + owner surfaced at the top of the popup so users
              // don't have to open the private map to know if a building is
              // classified as asbestos or who owns it.
              { label: 'Presencia de amianto', value: PRESENCE_LABEL[inventoryProps.is_asbestos] || null },
              { label: 'Titularidad', value: inventoryProps.owner },
              { label: 'Provincia', value: inventoryProps.Provincia },
              { label: 'Municipio', value: inventoryProps.Municipio },
              { label: 'Dirección', value: inventoryProps.address },
              { label: 'Ref. Catastral', value: inventoryProps.reference },
              { label: 'Uso actual del edificio', value: inventoryProps.currentUse },
              { label: 'Fecha de construcción', value: inventoryProps.beginning },
              { label: 'Antigüedad', value: inventoryProps.age ? `${inventoryProps.age} años` : null },
              { label: 'Área', value: inventoryProps.a_total != null ? `${Number(inventoryProps.a_total).toLocaleString('es-ES', { maximumFractionDigits: 2 })} m²` : null },
              { label: 'Catastro online', value: inventoryProps.Link_catastro, link: true },
              { label: 'Imagen del inmueble', value: inventoryProps.Link_image, link: true },
              { label: 'Superficie de amianto', value: inventoryProps.area_amianto ? `${Number(inventoryProps.area_amianto).toLocaleString('es-ES', { maximumFractionDigits: 2 })} m²` : null },
              { label: 'Porcentaje de ocupación de amianto', value: inventoryProps.amianto_percentage ? `${Number(inventoryProps.amianto_percentage).toLocaleString('es-ES', { maximumFractionDigits: 2 })} %` : null },
              { label: 'Prioridad de retirada', value: inventoryProps.priority },
              { label: 'Calendario de retirada', value: inventoryProps.date_withdrawal },
              { label: 'Coste estimado de retirada', value: inventoryProps.withdrawal_cost ? `${Number(inventoryProps.withdrawal_cost).toLocaleString('es-ES', { maximumFractionDigits: 2 })} €` : null },
              { label: 'Coste estimado de la mano de obra', value: inventoryProps.labor_cost ? `${Number(inventoryProps.labor_cost).toLocaleString('es-ES', { maximumFractionDigits: 2 })} €` : null },
              { label: 'Coste estimado de nueva cubierta', value: inventoryProps.new_cover_cost ? `${Number(inventoryProps.new_cover_cost).toLocaleString('es-ES', { maximumFractionDigits: 2 })} €` : null },
              { label: 'Coste estimado de licencia de obra', value: inventoryProps.building_license_cost ? `${Number(inventoryProps.building_license_cost).toLocaleString('es-ES', { maximumFractionDigits: 2 })} €` : null },
              { label: 'Coste estimado de nueva cubierta básica', value: inventoryProps.new_basic_cover_cost ? `${Number(inventoryProps.new_basic_cover_cost).toLocaleString('es-ES', { maximumFractionDigits: 2 })} €` : null },
              { label: 'Coste estimado de licencia de obra básica', value: inventoryProps.basic_building_license_cost ? `${Number(inventoryProps.basic_building_license_cost).toLocaleString('es-ES', { maximumFractionDigits: 2 })} €` : null },
              { label: 'Estado', value: inventoryProps.status },
            ]
              .filter((f) => f.value != null && f.value !== '' && f.value !== 'null')
              .map((field) => (
                <Box key={field.label}>
                  <Text size="sm" c="rgba(255, 255, 255, 0.6)" mb={4}>
                    {field.label}
                  </Text>
                  {field.link ? (
                    <Text
                      size="md"
                      c={GREEN}
                      component="a"
                      href={String(field.value)}
                      target="_blank"
                      style={{ wordBreak: 'break-all' }}
                    >
                      {String(field.value)}
                    </Text>
                  ) : (
                    <Text size="md" c="#fff">
                      {String(field.value)}
                    </Text>
                  )}
                </Box>
              ))}

            {/* Report button */}
            <Button
              fullWidth
              size="lg"
              mt="md"
              onClick={() => setModalMode('report')}
              styles={{
                root: {
                  backgroundColor: GREEN,
                  color: '#000',
                  '&:hover': { backgroundColor: '#00cc00' },
                },
              }}
            >
              {t('publicMap.reportAsbestos', 'Reportar Amianto')}
            </Button>
          </Stack>
          </ScrollArea.Autosize>
        )}

        {/* Cadastral Info View */}
        {modalMode === 'cadastral' && (
          <Stack gap="md">
            {cadastralLoading ? (
              <Group justify="center" p="xl">
                <Loader color="green" />
                <Text c="rgba(255, 255, 255, 0.7)">
                  {t('publicMap.loadingCadastral', 'Obteniendo información catastral...')}
                </Text>
              </Group>
            ) : cadastralData ? (
              <>
                {/* Facade photo */}
                <Box
                  style={{
                    borderRadius: 8,
                    overflow: 'hidden',
                    border: '1px solid rgba(0, 255, 0, 0.3)',
                    background: 'rgba(0, 0, 0, 0.3)',
                  }}
                >
                  <img
                    src={`https://ovc.catastro.meh.es/OVCServWeb/OVCWcfLibres/OVCFotoFachada.svc/RecuperarFotoFachadaGet?ReferenciaCatastral=${cadastralData.refCatastral}`}
                    alt="Fachada"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '200px',
                      objectFit: 'contain',
                      display: 'block',
                      margin: '0 auto',
                    }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none'
                    }}
                  />
                </Box>

                {/* Cadastral reference */}
                <Box>
                  <Text size="sm" c="rgba(255, 255, 255, 0.6)" mb={4}>
                    {t('publicMap.cadastralReference', 'Referencia Catastral')}
                  </Text>
                  <Text size="lg" fw={600} c="#fff">
                    {cadastralData.refCatastral}
                  </Text>
                </Box>

                {/* Location info */}
                {(cadastralData.municipio || cadastralData.address) && (
                  <Box>
                    <Text size="sm" c="rgba(255, 255, 255, 0.6)" mb={4}>
                      {t('publicMap.location', 'Ubicación')}
                    </Text>
                    <Text size="md" c="#fff">
                      {cadastralData.address || `${cadastralData.municipio}${cadastralData.provincia ? ` (${cadastralData.provincia})` : ''}`}
                    </Text>
                  </Box>
                )}

                {/* Property details in a grid */}
                <Group grow gap="md">
                  {/* Clase */}
                  {cadastralData.clase && (
                    <Box>
                      <Text size="sm" c="rgba(255, 255, 255, 0.6)" mb={4}>
                        {t('publicMap.propertyClass', 'Clase')}
                      </Text>
                      <Text size="md" c="#fff">
                        {cadastralData.clase === 'UR' ? 'Urbano' : cadastralData.clase === 'RU' ? 'Rústico' : cadastralData.clase}
                      </Text>
                    </Box>
                  )}

                  {/* Main use */}
                  {cadastralData.uso && (
                    <Box>
                      <Text size="sm" c="rgba(255, 255, 255, 0.6)" mb={4}>
                        {t('publicMap.mainUse', 'Uso principal')}
                      </Text>
                      <Text size="md" c="#fff">
                        {cadastralData.uso}
                      </Text>
                    </Box>
                  )}
                </Group>

                <Group grow gap="md">
                  {/* Built surface */}
                  {cadastralData.superficie && (
                    <Box>
                      <Text size="sm" c="rgba(255, 255, 255, 0.6)" mb={4}>
                        {t('publicMap.builtSurface', 'Superficie construida')}
                      </Text>
                      <Text size="md" c="#fff">
                        {cadastralData.superficie.toLocaleString('es-ES')} m²
                      </Text>
                    </Box>
                  )}

                  {/* Construction year */}
                  {cadastralData.añoConstruccion && (
                    <Box>
                      <Text size="sm" c="rgba(255, 255, 255, 0.6)" mb={4}>
                        {t('publicMap.constructionYear', 'Año construcción')}
                      </Text>
                      <Text size="md" c="#fff">
                        {cadastralData.añoConstruccion}
                      </Text>
                    </Box>
                  )}
                </Group>

                {/* Coordinates */}
                {selectedCoords && (
                  <Box>
                    <Text size="sm" c="rgba(255, 255, 255, 0.6)" mb={4}>
                      {t('publicMap.coordinates', 'Coordenadas')}
                    </Text>
                    <Text size="sm" c="rgba(255, 255, 255, 0.8)" ff="monospace">
                      {selectedCoords.lat.toFixed(6)}, {selectedCoords.lng.toFixed(6)}
                    </Text>
                  </Box>
                )}

                {/* Action buttons */}
                <Group gap="sm" mt="md">
                  <Button
                    variant="subtle"
                    size="sm"
                    leftSection={<IconExternalLink size={16} />}
                    component="a"
                    href={`https://www1.sedecatastro.gob.es/CYCBienInmueble/OVCListaBienes.aspx?rc1=${cadastralData.refCatastral.slice(0, 7)}&rc2=${cadastralData.refCatastral.slice(7)}`}
                    target="_blank"
                    styles={{
                      root: { color: 'rgba(255, 255, 255, 0.7)', '&:hover': { color: GREEN } },
                    }}
                  >
                    {t('publicMap.viewInCatastro', 'Ver en Catastro')}
                  </Button>
                </Group>

                {/* Report button */}
                <Button
                  fullWidth
                  size="lg"
                  mt="md"
                  onClick={() => setModalMode('report')}
                  styles={{
                    root: {
                      backgroundColor: GREEN,
                      color: '#000',
                      '&:hover': { backgroundColor: '#00cc00' },
                    },
                  }}
                >
                  {t('publicMap.reportAsbestos', 'Reportar Amianto')}
                </Button>
              </>
            ) : (
              <>
                {/* No cadastral data found */}
                <Alert
                  color="yellow"
                  styles={{
                    root: {
                      background: 'rgba(255, 193, 7, 0.1)',
                      border: '1px solid rgba(255, 193, 7, 0.3)',
                    },
                    message: { color: 'rgba(255, 255, 255, 0.9)' },
                  }}
                >
                  {t('publicMap.noCadastralData', 'No se encontró información catastral para esta ubicación.')}
                </Alert>

                {/* Still allow reporting */}
                <Button
                  fullWidth
                  size="lg"
                  mt="md"
                  onClick={() => setModalMode('report')}
                  styles={{
                    root: {
                      backgroundColor: GREEN,
                      color: '#000',
                      '&:hover': { backgroundColor: '#00cc00' },
                    },
                  }}
                >
                  {t('publicMap.reportAnywayButton', 'Reportar de todos modos')}
                </Button>
              </>
            )}
          </Stack>
        )}

        {/* Report Form View */}
        {modalMode === 'report' && (
        <Stack gap="md">
          {/* Back button */}
          <Button
            variant="subtle"
            size="sm"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => setModalMode(inventoryProps ? 'inventory' : 'cadastral')}
            styles={{
              root: { color: 'rgba(255, 255, 255, 0.7)', '&:hover': { color: GREEN }, alignSelf: 'flex-start' },
            }}
          >
            {t('publicMap.backToInfo', 'Volver')}
          </Button>

          {/* Address field - auto-filled from reverse geocoding on map click, editable */}
          <TextInput
            label={t('publicReport.address', 'Dirección')}
            placeholder={t(
              'publicReport.addressPlaceholder',
              'Dirección de la ubicación seleccionada'
            )}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            rightSection={addressLoading ? <Loader size="xs" color="green" /> : null}
            leftSection={<IconMap size={16} color={GREEN} />}
            styles={{
              label: { color: 'rgba(255, 255, 255, 0.9)' },
              input: {
                background: 'rgba(0, 0, 0, 0.5)',
                border: '1px solid rgba(0, 255, 0, 0.3)',
                color: '#fff',
                '&::placeholder': { color: 'rgba(255, 255, 255, 0.4)' },
                '&:focus': { borderColor: GREEN },
              },
            }}
          />

          {/* Message field */}
          <Textarea
            label={t('publicReport.message', 'Mensaje')}
            placeholder={t(
              'publicReport.messagePlaceholder',
              'Describa la situación del amianto detectado'
            )}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            minRows={3}
            styles={{
              label: { color: 'rgba(255, 255, 255, 0.9)' },
              input: {
                background: 'rgba(0, 0, 0, 0.5)',
                border: '1px solid rgba(0, 255, 0, 0.3)',
                color: '#fff',
                '&::placeholder': { color: 'rgba(255, 255, 255, 0.4)' },
                '&:focus': { borderColor: GREEN },
              },
            }}
          />

          {/* Photos - optional */}
          <Box>
            <Text size="sm" fw={500} mb="xs" c="rgba(255, 255, 255, 0.9)">
              {t('publicReport.photos', 'Fotografías')} ({t('publicReport.optional', 'opcional')})
            </Text>
            <Group gap="xs" mb="xs">
              {images.map((img, index) => (
                <Box key={index} pos="relative" style={{ width: 80, height: 80 }}>
                  <Image
                    src={img.preview}
                    alt={`Preview ${index + 1}`}
                    radius="sm"
                    fit="cover"
                    h={80}
                    w={80}
                  />
                  <CloseButton
                    size="xs"
                    pos="absolute"
                    top={2}
                    right={2}
                    onClick={() => removeImage(index)}
                    style={{ background: 'rgba(0, 0, 0, 0.7)', color: '#fff', borderRadius: '50%' }}
                  />
                </Box>
              ))}
              {images.length < 5 && (
                <FileButton onChange={handleImageSelect} accept="image/*" multiple>
                  {(props) => (
                    <Box
                      {...props}
                      style={{
                        width: 80,
                        height: 80,
                        border: '2px dashed rgba(0, 255, 0, 0.4)',
                        borderRadius: 8,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        flexDirection: 'column',
                        gap: 4,
                        background: 'rgba(0, 0, 0, 0.3)',
                      }}
                    >
                      <IconUpload size={20} color={GREEN} />
                      <Text size="xs" c="rgba(255, 255, 255, 0.6)">
                        {t('publicReport.uploadPhoto', 'Subir')}
                      </Text>
                    </Box>
                  )}
                </FileButton>
              )}
            </Group>
          </Box>

          <Divider
            label={t('publicReport.contactOptionalLabel', 'Datos de contacto (opcional)')}
            labelPosition="center"
            styles={{
              label: { color: 'rgba(255, 255, 255, 0.6)', fontSize: '12px' },
              root: { borderColor: 'rgba(0, 255, 0, 0.2)' },
            }}
          />

          {/* Contact fields - optional */}
          <TextInput
            label={t('publicReport.name', 'Nombre')}
            placeholder={t('publicReport.namePlaceholder', 'Su nombre')}
            value={reporterName}
            onChange={(e) => setReporterName(e.target.value)}
            styles={{
              label: { color: 'rgba(255, 255, 255, 0.9)' },
              input: {
                background: 'rgba(0, 0, 0, 0.5)',
                border: '1px solid rgba(0, 255, 0, 0.3)',
                color: '#fff',
                '&::placeholder': { color: 'rgba(255, 255, 255, 0.4)' },
                '&:focus': { borderColor: GREEN },
              },
            }}
          />

          <TextInput
            label={t('publicReport.email', 'Email')}
            placeholder={t('publicReport.emailPlaceholder', 'Su correo electrónico')}
            value={reporterEmail}
            onChange={(e) => setReporterEmail(e.target.value)}
            type="email"
            error={
              reporterEmail && !emailValid
                ? t('publicReport.invalidEmail', 'Por favor, introduzca un email válido')
                : undefined
            }
            styles={{
              label: { color: 'rgba(255, 255, 255, 0.9)' },
              input: {
                background: 'rgba(0, 0, 0, 0.5)',
                border: '1px solid rgba(0, 255, 0, 0.3)',
                color: '#fff',
                '&::placeholder': { color: 'rgba(255, 255, 255, 0.4)' },
                '&:focus': { borderColor: GREEN },
              },
            }}
          />

          <TextInput
            label={t('publicReport.phone', 'Teléfono')}
            placeholder={t('publicReport.phonePlaceholder', 'Su teléfono')}
            value={reporterPhone}
            onChange={(e) => setReporterPhone(e.target.value)}
            error={
              reporterPhone && !phoneValid
                ? t('publicReport.invalidPhone', 'Por favor, introduzca un teléfono válido')
                : undefined
            }
            styles={{
              label: { color: 'rgba(255, 255, 255, 0.9)' },
              input: {
                background: 'rgba(0, 0, 0, 0.5)',
                border: '1px solid rgba(0, 255, 0, 0.3)',
                color: '#fff',
                '&::placeholder': { color: 'rgba(255, 255, 255, 0.4)' },
                '&:focus': { borderColor: GREEN },
              },
            }}
          />

          {/* Data protection checkbox */}
          <Checkbox
            label={t('publicReport.dataProtection', 'Acepto la política de protección de datos')}
            checked={dataProtectionAccepted}
            onChange={(e) => setDataProtectionAccepted(e.target.checked)}
            required
            styles={{
              label: { color: 'rgba(255, 255, 255, 0.8)' },
              input: {
                backgroundColor: dataProtectionAccepted ? GREEN : 'rgba(0, 0, 0, 0.5)',
                borderColor: 'rgba(0, 255, 0, 0.5)',
              },
            }}
          />

          {error && (
            <Alert
              color="red"
              title={t('publicReport.errorTitle', 'Error')}
              styles={{
                root: {
                  background: 'rgba(255, 0, 0, 0.2)',
                  border: '1px solid rgba(255, 0, 0, 0.5)',
                },
                title: { color: '#ff6b6b' },
                message: { color: 'rgba(255, 255, 255, 0.9)' },
              }}
            >
              {error}
            </Alert>
          )}

          <Group justify="flex-end">
            <Button
              variant="outline"
              onClick={handleCloseModal}
              styles={{
                root: {
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  color: 'rgba(255, 255, 255, 0.8)',
                  '&:hover': { background: 'rgba(255, 255, 255, 0.1)' },
                },
              }}
            >
              {t('common.cancel', 'Cancelar')}
            </Button>
            <Button
              onClick={handleSubmitReport}
              disabled={!canSubmit}
              loading={submitting}
              styles={{
                root: {
                  backgroundColor: GREEN,
                  color: '#000',
                  '&:hover': { backgroundColor: '#00cc00' },
                  '&:disabled': {
                    backgroundColor: 'rgba(0, 255, 0, 0.3)',
                    color: 'rgba(0, 0, 0, 0.5)',
                  },
                },
              }}
            >
              {t('publicReport.submit', 'Enviar')}
            </Button>
          </Group>
        </Stack>
        )}
      </Modal>
    </Box>
  )
}
